// Minecraft course demo: a grid of blocks you can mine by hand, one tap at a
// time, next to a two-line loop that clears the whole grid on its own.
//
// Tapping is the "before", running the loop is the "after", and the gap between
// them is the first lesson of the syllabus.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { Pickaxe as PickaxeIcon, Play } from 'lucide-react';
import { PICKAXE_MODEL, usePickaxeRest } from '../MinecraftIsland';
import {
  Fitted,
  HaloRing,
  PLATFORM_TOP,
  ShadowBlob,
  useCloned,
  usePointerCursor,
} from '../helpers';
import DemoShell from './DemoShell';

/* ── The grid ──
   Four across reads as a row on a phone without the blocks getting too small
   to hit; three deep keeps the whole thing inside the platform's 2.5 radius. */
const COLS = 4;
const ROWS = 3;
export const BLOCK_COUNT = COLS * ROWS;
const BLOCK = 0.6;
const PITCH = 0.76;

/** Centre of block `i` on the platform, as [x, z]. */
const blockXZ = (i) => [
  ((i % COLS) - (COLS - 1) / 2) * PITCH,
  (Math.floor(i / COLS) - (ROWS - 1) / 2) * PITCH,
];

/* ── Swing ──
   One strike per target rather than the island's idle loop: the tool travels to
   the block during the wind-up, lands exactly at IMPACT_AT, and recovers while
   the next target is already being chosen. */
const WINDUP_FRACTION = 0.5;
const STRIKE_FRACTION = 0.14;
const RECOVERY_FRACTION = 1 - WINDUP_FRACTION - STRIKE_FRACTION;
const IMPACT_AT = WINDUP_FRACTION + STRIKE_FRACTION;
const SWING_BACK = -0.55;
const SWING_TRAVEL = 1.25;
const STRIKE_ANGLE = SWING_BACK + SWING_TRAVEL;
const SWING_AXIS = new THREE.Vector3(0, 0, 1);

const swingAngle = (p) => {
  if (p < WINDUP_FRACTION) return SWING_BACK * Math.pow(p / WINDUP_FRACTION, 2);
  if (p < IMPACT_AT) {
    const s = (p - WINDUP_FRACTION) / STRIKE_FRACTION;
    return SWING_BACK + SWING_TRAVEL * Math.pow(s, 0.6);
  }
  const r = (p - IMPACT_AT) / RECOVERY_FRACTION;
  return STRIKE_ANGLE * Math.pow(1 - r, 3);
};

const PICK_LENGTH = 1.5;
/** Sideways / toward-camera offset of the swing from the block it is hitting. */
const PIVOT_OFFSET = [0.62, 0.3];
const STRIKE_CLEARANCE = 0.03;
const PIVOT_DIP = 0.22;
/** Vertical drop from pivot to head tip at impact — half the tool's length,
    foreshortened by the strike angle. */
const REACH = (PICK_LENGTH / 2) * Math.cos(STRIKE_ANGLE);
const STRIKE_PIVOT = BLOCK + REACH + STRIKE_CLEARANCE;

/** Seconds per strike. Running the loop is deliberately the faster one — that
    difference is the whole argument the section is making. */
const STRIKE_SECONDS = { manual: 0.44, code: 0.2 };

/* ── Blocks ───────────────────────────────────────────────────────────────── */

const Block = ({ index, top, mined, accent, reduced, onTap }) => {
  const mesh = useRef();
  const core = useRef();
  const seam = useRef();
  // Presence: 1 = standing, 0 = mined out. Damped, so the same value plays the
  // break and the rebuild without either needing its own animation.
  const presence = useRef(1);
  const [hovered, setHovered] = useState(false);
  usePointerCursor(hovered && !mined);

  const [x, z] = useMemo(() => blockXZ(index), [index]);

  useFrame((state, delta) => {
    const m = mesh.current;
    if (!m) return;
    const target = mined ? 0 : 1;
    presence.current = reduced
      ? target
      : THREE.MathUtils.damp(presence.current, target, 9, Math.min(delta, 0.1));
    // Snap the last sliver shut, or a mined block lingers as a visible speck.
    if (Math.abs(presence.current - target) < 0.01) presence.current = target;

    m.visible = presence.current > 0.005;
    if (!m.visible) return;

    const p = presence.current;
    const lift = hovered && !mined ? 0.05 : 0;
    m.scale.setScalar(p * (1 + lift));
    m.position.y = top + (BLOCK / 2) * p + lift * 0.1;
    // A quarter turn as it crumbles, unwound as it comes back.
    m.rotation.y = (1 - p) * Math.PI * 0.5;
    if (core.current) core.current.emissiveIntensity = 0.06 + (hovered && !mined ? 0.3 : 0);
    if (seam.current) seam.current.opacity = 0.5 + (hovered && !mined ? 0.22 : 0);
  });

  return (
    <mesh
      ref={mesh}
      position={[x, top + BLOCK / 2, z]}
      onPointerOver={(e) => { e.stopPropagation(); setHovered(true); }}
      onPointerOut={() => setHovered(false)}
      onClick={(e) => { e.stopPropagation(); if (!mined) onTap(index); }}
    >
      <boxGeometry args={[BLOCK, BLOCK, BLOCK]} />
      <meshStandardMaterial
        ref={core}
        color="#5B6B7A"
        emissive={accent}
        emissiveIntensity={0.06}
        roughness={0.85}
        metalness={0.05}
      />
      {/* Ore seam catching the light */}
      <mesh scale={1.01}>
        <boxGeometry args={[BLOCK, BLOCK, BLOCK]} />
        <meshBasicMaterial
          ref={seam}
          color={accent}
          wireframe
          transparent
          opacity={0.5}
          toneMapped={false}
        />
      </mesh>
    </mesh>
  );
};

/* ── Debris ───────────────────────────────────────────────────────────────
   Three emitters on rotation. One would be enough for hand-mining, but the
   loop breaks a block every 0.2s and a single pool would cut its own chips off
   mid-flight. */
const EMITTERS = 3;
const CHIPS = 9;
const CHIP_LIFE = 0.7;

const Debris = React.forwardRef(({ accent }, api) => {
  const groups = useRef([]);
  const state = useRef(
    new Array(EMITTERS).fill(0).map(() => ({ t: -1, x: 0, y: 0, z: 0 }))
  );
  const next = useRef(0);

  const chips = useMemo(
    () =>
      new Array(CHIPS).fill(0).map((_, i) => {
        const angle = (i / CHIPS) * Math.PI * 2 + Math.random();
        return {
          key: i,
          dir: [Math.cos(angle), 0.9 + Math.random() * 0.7, Math.sin(angle)],
          speed: 1.4 + Math.random() * 1.1,
          spin: (Math.random() - 0.5) * 7,
          size: 0.05 + Math.random() * 0.055,
        };
      }),
    []
  );

  React.useImperativeHandle(api, () => ({
    burst(x, y, z) {
      const slot = state.current[next.current];
      slot.t = 0;
      slot.x = x;
      slot.y = y;
      slot.z = z;
      next.current = (next.current + 1) % EMITTERS;
    },
  }), []);

  useFrame((_, delta) => {
    state.current.forEach((slot, e) => {
      const group = groups.current[e];
      if (!group) return;
      if (slot.t < 0) { group.visible = false; return; }

      slot.t += delta;
      if (slot.t > CHIP_LIFE) { slot.t = -1; group.visible = false; return; }

      group.visible = true;
      group.position.set(slot.x, slot.y, slot.z);
      const life = slot.t / CHIP_LIFE;

      group.children.forEach((chip, i) => {
        const c = chips[i];
        const t = life * c.speed;
        chip.position.set(
          c.dir[0] * t * 0.45,
          // Up, then gravity takes it back down.
          c.dir[1] * t * 0.55 - 1.6 * t * t * 0.5,
          c.dir[2] * t * 0.45
        );
        chip.rotation.x = c.spin * t;
        chip.rotation.y = c.spin * t * 0.7;
        chip.material.opacity = Math.max(0, 1 - life * 1.15);
      });
    });
  });

  return (
    <>
      {new Array(EMITTERS).fill(0).map((_, e) => (
        <group key={e} ref={(g) => { groups.current[e] = g; }} visible={false}>
          {chips.map(({ key, size }) => (
            <mesh key={key} scale={size}>
              <boxGeometry args={[1, 1, 1]} />
              <meshStandardMaterial
                color={accent}
                roughness={0.7}
                transparent
                opacity={0}
                toneMapped={false}
              />
            </mesh>
          ))}
        </group>
      ))}
    </>
  );
});

/* ── The tool ─────────────────────────────────────────────────────────────
   Owns the queue: it pulls the next target, flies to it during the wind-up,
   and reports the hit at the exact frame the head lands. The block only
   disappears because the pickaxe said so, which is what makes the two read as
   one action instead of two things happening at once. */
const Pickaxe = ({ top, queueRef, speedRef, onImpact, debrisRef, reduced }) => {
  const object = useCloned(PICKAXE_MODEL);
  const group = useRef();
  const rest = usePickaxeRest(object);
  const swingQuat = useMemo(() => new THREE.Quaternion(), []);
  const active = useRef(null);
  const home = useMemo(() => new THREE.Vector3(PIVOT_OFFSET[0], 0, PIVOT_OFFSET[1] + 0.9), []);

  useFrame((_, delta) => {
    const g = group.current;
    if (!g) return;
    const step = Math.min(delta, 0.1);

    if (!active.current && queueRef.current.length) {
      const id = queueRef.current.shift();
      const [bx, bz] = blockXZ(id);
      active.current = {
        id,
        t: 0,
        hit: false,
        // Fly in from wherever the last strike left it.
        from: new THREE.Vector2(g.position.x, g.position.z),
        to: new THREE.Vector2(bx + PIVOT_OFFSET[0], bz + PIVOT_OFFSET[1]),
        block: [bx, bz],
      };
      if (reduced) {
        // No swing to hang the impact off — break it now and skip the flight.
        onImpact(id, bx, top + BLOCK / 2, bz);
        active.current = null;
      }
    }

    const a = active.current;

    if (!a) {
      // Idle: drift back over the grid, tool at rest.
      g.quaternion.copy(rest);
      g.position.x = THREE.MathUtils.damp(g.position.x, home.x, 3, step);
      g.position.z = THREE.MathUtils.damp(g.position.z, home.z, 3, step);
      g.position.y = THREE.MathUtils.damp(
        g.position.y,
        top + STRIKE_PIVOT + PIVOT_DIP * STRIKE_ANGLE,
        3,
        step
      );
      return;
    }

    a.t = Math.min(1, a.t + step / speedRef.current);
    const swing = swingAngle(a.t);

    // Arrive exactly as the head lands: the travel is spent on the wind-up.
    const travel = Math.min(1, a.t / IMPACT_AT);
    const eased = travel * travel * (3 - 2 * travel);
    g.position.x = a.from.x + (a.to.x - a.from.x) * eased;
    g.position.z = a.from.y + (a.to.y - a.from.y) * eased;
    g.position.y = top + STRIKE_PIVOT + PIVOT_DIP * (STRIKE_ANGLE - swing);

    swingQuat.setFromAxisAngle(SWING_AXIS, swing);
    g.quaternion.copy(rest).premultiply(swingQuat);

    if (!a.hit && a.t >= IMPACT_AT) {
      a.hit = true;
      onImpact(a.id, a.block[0], top + BLOCK / 2, a.block[1]);
      if (debrisRef.current) debrisRef.current.burst(a.block[0], top + BLOCK * 0.6, a.block[1]);
    }
    if (a.t >= 1) active.current = null;
  });

  return (
    <group ref={group} position={[home.x, top + STRIKE_PIVOT + PIVOT_DIP, home.z]}>
      <Fitted object={object} size={PICK_LENGTH} fit="max" align="center" />
    </group>
  );
};

/** Frames the platform. A phone gets the shot from further back and higher up:
    the same camera that composes well on a wide card crops the outer column of
    blocks once the viewport turns portrait. */
const Framing = ({ top }) => {
  const { camera, size } = useThree();

  useEffect(() => {
    const narrow = size.width < 640;
    camera.position.set(narrow ? 0.2 : 0.4, top + (narrow ? 4.4 : 4.1), narrow ? 7.2 : 6.6);
    camera.lookAt(0, top + 0.5, 0);
    camera.updateProjectionMatrix();
  }, [camera, size.width, size.height, top]);

  return null;
};

/* ── Scene ────────────────────────────────────────────────────────────────
   No Environment probe and no post-processing, unlike the courses world: this
   canvas is the first thing an ad click renders, so it pays for its lighting
   with three lights instead of a 2 MB HDR and a composer pass. */
const Scene = ({ mined, accent, accentInk, queueRef, speedRef, onImpact, reduced }) => {
  const platform = useCloned('/GodotBotPlatform.glb');
  const [top, setTop] = useState(PLATFORM_TOP);
  const debrisRef = useRef();

  return (
    <>
      <Framing top={top} />

      <ambientLight intensity={0.5} />
      <hemisphereLight args={['#9DC3E6', '#0A1020', 0.6]} />
      <directionalLight position={[5, 9, 6]} intensity={1.25} />

      <Fitted object={platform} size={5} fit="max" onFit={({ height }) => setTop(height)} />
      <ShadowBlob radius={3} opacity={0.55} position={[0, top + 0.012, 0]} />
      <HaloRing accent={accent} accentInk={accentInk} radius={2.45} position={[0, top - 0.4, 0]} />

      {new Array(BLOCK_COUNT).fill(0).map((_, i) => (
        <Block
          key={i}
          index={i}
          top={top}
          mined={mined.has(i)}
          accent={accent}
          reduced={reduced}
          onTap={(id) => queueRef.current.push(id)}
        />
      ))}

      <Pickaxe
        top={top}
        queueRef={queueRef}
        speedRef={speedRef}
        onImpact={onImpact}
        debrisRef={debrisRef}
        reduced={reduced}
      />
      <Debris ref={debrisRef} accent={accent} />

      <pointLight color={accent} intensity={1.8} distance={7} decay={2} position={[0, top + 1.6, 1.2]} />
    </>
  );
};

/* ── Demo ─────────────────────────────────────────────────────────────────── */

const CODE_LINES = ['for block in world.blocks:', '    agent.mine(block)'];

const MineDemo = ({ accent, accentInk, reduced = false, onFirstUse, onRun }) => {
  const [mined, setMined] = useState(() => new Set());
  const [running, setRunning] = useState(false);
  const queueRef = useRef([]);
  const speedRef = useRef(STRIKE_SECONDS.manual);
  const announced = useRef(false);

  const remaining = BLOCK_COUNT - mined.size;
  const cleared = remaining === 0;

  const mine = useCallback((id) => {
    setMined((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    if (!announced.current) {
      announced.current = true;
      if (onFirstUse) onFirstUse();
    }
  }, [onFirstUse]);

  // Without a canvas nothing is driving the queue, so drain it on a timer at
  // the same cadence the pickaxe would have.
  const [hasCanvas, setHasCanvas] = useState(false);
  useEffect(() => {
    if (hasCanvas) return undefined;
    const id = setInterval(() => {
      const next = queueRef.current.shift();
      if (next !== undefined) mine(next);
    }, 170);
    return () => clearInterval(id);
  }, [hasCanvas, mine]);

  useEffect(() => {
    if (running && cleared) setRunning(false);
  }, [running, cleared]);

  const tap = useCallback((id) => {
    if (running) return;
    speedRef.current = STRIKE_SECONDS.manual;
    queueRef.current.push(id);
  }, [running]);

  /** The DOM equivalent of tapping a block — the keyboard path into the demo. */
  const mineOne = useCallback(() => {
    const queued = new Set([...mined, ...queueRef.current]);
    for (let i = 0; i < BLOCK_COUNT; i += 1) {
      if (!queued.has(i)) { tap(i); return; }
    }
  }, [mined, tap]);

  const runCode = useCallback(() => {
    if (running || cleared) return;
    speedRef.current = STRIKE_SECONDS.code;
    queueRef.current = [];
    for (let i = 0; i < BLOCK_COUNT; i += 1) if (!mined.has(i)) queueRef.current.push(i);
    setRunning(true);
    if (onRun) onRun();
  }, [running, cleared, mined, onRun]);

  const rebuild = useCallback(() => {
    queueRef.current = [];
    setRunning(false);
    setMined(new Set());
  }, []);

  return (
    <DemoShell
      camera={{ fov: 42, near: 0.1, far: 60, position: [0.4, 4.4, 6.6] }}
      scene={
        <SceneMount onMount={setHasCanvas}>
          <Scene
            mined={mined}
            accent={accent}
            accentInk={accentInk}
            queueRef={queueRef}
            speedRef={speedRef}
            onImpact={mine}
            reduced={reduced}
          />
        </SceneMount>
      }
      fallback={
        // Same information, no WebGL: the grid as flat tiles.
        <div className="demo-flat" style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)` }} aria-hidden="true">
          {new Array(BLOCK_COUNT).fill(0).map((_, i) => (
            <span key={i} className={mined.has(i) ? 'tile gone' : 'tile'} />
          ))}
        </div>
      }
      status={
        <>
          <PickaxeIcon size={16} aria-hidden="true" />
          נכרו <strong>{mined.size}</strong> מתוך {BLOCK_COUNT} בלוקים
          {cleared && <em> — השטח נוקה!</em>}
        </>
      }
      code={{
        tag: 'CODE BUILDER',
        label: 'קוד פייתון לדוגמה: לולאה שכורה את כל הבלוקים',
        lines: CODE_LINES,
        activeLine: running ? 1 : null,
      }}
      controls={
        <>
          <button type="button" className="gg-btn-ghost" onClick={mineOne} disabled={running || cleared}>
            <PickaxeIcon size={16} aria-hidden="true" /> כרו בלוק
          </button>
          <button type="button" className="gg-btn" onClick={runCode} disabled={running || cleared}>
            <Play size={16} aria-hidden="true" /> {running ? 'רץ...' : 'הריצו את הקוד'}
          </button>
        </>
      }
      hint={
        cleared
          ? 'שתי שורות קוד ניקו את כל השטח. זה בדיוק מה שלומדים בשיעור על לולאות.'
          : 'אפשר גם ללחוץ ישירות על בלוק בסצנה. ואז לנסות את הקוד.'
      }
      onReset={rebuild}
      resetDisabled={mined.size === 0}
      resetLabel="בנייה מחדש"
    />
  );
};

/**
 * Tells the demo whether a canvas actually mounted.
 *
 * The shell decides that on its own (device, connection, viewport), and the
 * queue needs a driver either way: the pickaxe advances it frame by frame when
 * there is 3D, a timer does it when there is not.
 */
const SceneMount = ({ onMount, children }) => {
  useEffect(() => {
    onMount(true);
    return () => onMount(false);
  }, [onMount]);
  return children;
};

export default MineDemo;
